import { useState } from "react";
import { useAuth, API } from "../App";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Copy, Download, Facebook, Instagram, ChevronDown, ChevronUp,
  Square, Smartphone, Layers, CheckCircle2, Sparkles, Loader2,
  X, MapPin, DollarSign, Home, Users, Wand2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const LOGO = "https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png";

const IMAGES = {
  agent_laptop: "https://images.unsplash.com/photo-1534243909760-a5009d375b0f?auto=format&fit=crop&w=1200&q=80",
  messy_desk: "https://images.unsplash.com/photo-1774352724311-ba2694681e83?auto=format&fit=crop&w=1200&q=80",
  luxury_home: "https://images.unsplash.com/photo-1771192442016-d4c78d5729eb?auto=format&fit=crop&w=1200&q=80",
  handshake: "https://images.unsplash.com/photo-1549923746-c502d488b3ea?auto=format&fit=crop&w=1200&q=80",
  office: "https://images.unsplash.com/photo-1762341116319-05a8355fcfc9?auto=format&fit=crop&w=1200&q=80",
};

const ADS = [
  // ─── FACEBOOK FEED ADS ───
  {
    id: "fb-feed-1",
    platform: "Facebook",
    format: "Feed (1:1)",
    type: "feed",
    image: IMAGES.messy_desk,
    headline: "Still chasing deadlines with spreadsheets?",
    primary_text: "You didn't get into real estate to spend hours tracking dates, chasing signatures, and manually emailing reminders.\n\nEasyOffice automates your entire transaction workflow — from contract to close. Upload a PDF, and AI extracts every deadline, price, and party in seconds.\n\nNo credit card needed. Set up in 2 minutes.",
    cta: "Get Started Free",
    link_description: "Free transaction management for real estate pros",
  },
  {
    id: "fb-feed-2",
    platform: "Facebook",
    format: "Feed (1:1)",
    type: "feed",
    image: IMAGES.agent_laptop,
    headline: "What if your transactions managed themselves?",
    primary_text: "Automated emails. Dynamic deadlines that shift when the closing date changes. AI that reads your contracts and fills in every detail.\n\nThat's EasyOffice — the transaction management platform built by agents, for agents.\n\nJoin hundreds of teams who close faster with less stress.",
    cta: "Try It Free",
    link_description: "AI-powered transaction management",
  },
  {
    id: "fb-feed-3",
    platform: "Facebook",
    format: "Feed (1:1)",
    type: "feed",
    image: IMAGES.handshake,
    headline: "Close more deals. Chase fewer deadlines.",
    primary_text: "Your clients deserve better than \"let me check on that and get back to you.\"\n\nWith EasyOffice, every task, email, and deadline is automated. Your clients get timely updates. You get your evenings back.\n\nFree to start. No credit card required.",
    cta: "Start Free Today",
    link_description: "Transaction management on autopilot",
  },

  // ─── INSTAGRAM STORY / REEL ADS ───
  {
    id: "ig-story-1",
    platform: "Instagram",
    format: "Story / Reel (9:16)",
    type: "story",
    image: IMAGES.luxury_home,
    headline: "Your transactions.\nOn autopilot.",
    primary_text: "Upload a contract PDF.\nAI extracts every date & detail.\nEmails fire automatically.\nDeadlines shift when plans change.\n\nFree to start. No credit card.",
    cta: "Try EasyOffice Free",
    hook: "POV: You just automated your entire transaction workflow",
  },
  {
    id: "ig-story-2",
    platform: "Instagram",
    format: "Story / Reel (9:16)",
    type: "story",
    image: IMAGES.office,
    headline: "Stop doing\nyour TC's job.",
    primary_text: "EasyOffice handles the busywork so you can focus on what actually makes you money — selling houses.\n\nAutomatic emails. Smart deadlines. Zero missed tasks.",
    cta: "Get Started Free",
    hook: "Real estate agents: this one's for you",
  },

  // ─── TIKTOK ADS ───
  {
    id: "tt-1",
    platform: "TikTok",
    format: "In-Feed (9:16)",
    type: "story",
    image: IMAGES.messy_desk,
    headline: "This is how most\nagents track transactions.",
    primary_text: "Sticky notes. Spreadsheets. 47 browser tabs.\n\nOr... you could use EasyOffice.\n\nUpload a contract. AI reads it. Tasks auto-assign. Emails send themselves. Deadlines update when things change.\n\nFree. No credit card. 2 minute setup.",
    cta: "Link in Bio",
    hook: "POV: Your transaction coordinator just quit and you need a backup plan",
  },
  {
    id: "tt-2",
    platform: "TikTok",
    format: "In-Feed (9:16)",
    type: "story",
    image: IMAGES.handshake,
    headline: "The secret weapon\ntop agents won't tell you about.",
    primary_text: "It's not a new CRM. It's not another lead gen tool.\n\nIt's a transaction manager that actually does the work for you.\n\nEasyOffice: AI reads your contracts, sends your emails, and tracks every deadline — automatically.",
    cta: "Try It Free",
    hook: "Realtors, stop scrolling. This will save you 4+ hours per deal.",
  },

  // ─── CAROUSEL ADS ───
  {
    id: "carousel-1",
    platform: "Facebook / Instagram",
    format: "Carousel (1:1)",
    type: "carousel",
    slides: [
      { image: IMAGES.messy_desk, text: "This is you tracking transactions with spreadsheets." },
      { image: IMAGES.agent_laptop, text: "This is you after switching to EasyOffice." },
      { image: IMAGES.luxury_home, text: "Upload a contract. AI extracts every deadline." },
      { image: IMAGES.handshake, text: "Automated emails. Zero missed deadlines." },
    ],
    headline: "From chaos to closing — in one click.",
    primary_text: "EasyOffice automates your entire transaction workflow. Upload a contract PDF and watch AI extract every date, party, and detail. Emails send themselves. Deadlines shift automatically.\n\nFree to start. No credit card needed.",
    cta: "Get Started Free",
  },
];

const copyText = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard!");
};

const PlatformBadge = ({ platform }) => {
  const colors = {
    Facebook: "bg-blue-100 text-blue-700 border-blue-200",
    Instagram: "bg-pink-100 text-pink-700 border-pink-200",
    TikTok: "bg-gray-100 text-gray-800 border-gray-300",
    "Facebook / Instagram": "bg-purple-100 text-purple-700 border-purple-200",
  };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${colors[platform] || "bg-gray-100 text-gray-700 border-gray-200"}`}>
      {platform}
    </span>
  );
};

const FormatBadge = ({ format }) => (
  <span className="text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#2C4C3B]/15 text-[#2C4C3B]/60 bg-[#2C4C3B]/5">
    {format}
  </span>
);

const AdCard = ({ ad }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden border border-[#2C4C3B]/10 hover:shadow-lg transition-shadow" data-testid={`ad-card-${ad.id}`}>
      <CardContent className="p-0">
        {/* Preview */}
        {ad.type === "carousel" ? (
          <div className="flex gap-1 overflow-x-auto p-3 bg-gray-50">
            {ad.slides.map((slide) => (
              <div key={slide.text} className="relative flex-shrink-0 w-48 h-48 rounded-lg overflow-hidden">
                <img src={slide.image} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-3">
                  <p className="text-white text-[11px] font-medium leading-tight">{slide.text}</p>
                </div>
              </div>
            ))}
          </div>
        ) : ad.type === "story" ? (
          <div className="relative mx-auto w-full max-w-[280px] aspect-[9/16] rounded-xl overflow-hidden my-3">
            <img src={ad.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/90 via-[#2C4C3B]/30 to-transparent flex flex-col justify-between p-5">
              {ad.hook && (
                <p className="text-white/80 text-[10px] font-medium bg-black/30 backdrop-blur-sm rounded-full px-3 py-1 self-start">{ad.hook}</p>
              )}
              <div>
                <h3 className="text-white text-xl font-bold leading-tight mb-2 whitespace-pre-line" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {ad.headline}
                </h3>
                <div className="flex items-center gap-2 mt-3">
                  <img src={LOGO} alt="EasyOffice" className="h-5 w-auto" />
                  <span className="text-white/70 text-[10px]">easyoffice.systems</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative aspect-square overflow-hidden">
            <img src={ad.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2C4C3B]/85 via-transparent to-transparent flex flex-col justify-end p-6">
              <h3 className="text-white text-xl font-bold leading-tight mb-1" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                {ad.headline}
              </h3>
              <div className="flex items-center gap-2 mt-2">
                <img src={LOGO} alt="EasyOffice" className="h-4 w-auto" />
                <span className="text-white/60 text-[10px]">easyoffice.systems</span>
              </div>
            </div>
          </div>
        )}

        {/* Copy Section */}
        <div className="p-4 space-y-3 border-t border-[#2C4C3B]/10">
          <div className="flex items-center gap-2 flex-wrap">
            <PlatformBadge platform={ad.platform} />
            <FormatBadge format={ad.format} />
          </div>

          <div>
            <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Headline</p>
            <p className="text-sm font-semibold text-[#2C4C3B]">{ad.headline.replace(/\n/g, " ")}</p>
          </div>

          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs font-semibold text-[#CC7753] hover:underline"
            >
              {expanded ? "Hide" : "Show"} Full Copy
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {expanded && (
              <div className="mt-2 space-y-2">
                <div className="bg-[#F9F6F0] rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Primary Text</p>
                  <p className="text-sm text-[#2C4C3B] whitespace-pre-line leading-relaxed">{ad.primary_text}</p>
                </div>
                {ad.link_description && (
                  <div className="bg-[#F9F6F0] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Link Description</p>
                    <p className="text-sm text-[#2C4C3B]">{ad.link_description}</p>
                  </div>
                )}
                {ad.hook && (
                  <div className="bg-[#F9F6F0] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Hook / Opening Line</p>
                    <p className="text-sm text-[#2C4C3B]">{ad.hook}</p>
                  </div>
                )}
                <div className="bg-[#F9F6F0] rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">CTA Button</p>
                  <p className="text-sm font-semibold text-[#CC7753]">{ad.cta}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => copyText(`${ad.headline.replace(/\n/g, " ")}\n\n${ad.primary_text}\n\nCTA: ${ad.cta}${ad.link_description ? `\n${ad.link_description}` : ""}`)}
              data-testid={`copy-${ad.id}`}
            >
              <Copy className="w-3 h-3 mr-1.5" /> Copy All Text
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => {
                const a = document.createElement("a");
                a.href = ad.image || ad.slides?.[0]?.image;
                a.target = "_blank";
                a.download = `easyoffice-${ad.id}.jpg`;
                a.click();
              }}
              data-testid={`download-${ad.id}`}
            >
              <Download className="w-3 h-3 mr-1.5" /> Image
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ──── AI Ad Generator Form ────
const GeneratorForm = ({ onGenerated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    property_address: "",
    price: "",
    bedrooms: "",
    bathrooms: "",
    sqft: "",
    key_features: "",
    target_audience: "",
    tone: "professional",
    listing_url: "",
  });

  const update = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const generate = async () => {
    if (!form.property_address.trim()) {
      toast.error("Property address is required");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/platform/generate-ads`, form, { withCredentials: true });
      const ads = res.data?.ads || [];
      if (ads.length === 0) {
        toast.error("No ads generated. Try again.");
      } else {
        toast.success(`Generated ${ads.length} custom ads!`);
        onGenerated(ads);
        setOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate ads");
    } finally {
      setLoading(false);
    }
  };

  const toneOptions = ["professional", "luxury", "casual", "urgent", "friendly", "bold"];

  return (
    <Card className="border border-dashed border-[#CC7753]/40 bg-gradient-to-br from-[#CC7753]/5 to-transparent mb-8" data-testid="ai-generator-card">
      <CardContent className="p-0">
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between p-5 text-left group"
          data-testid="ai-generator-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#CC7753]/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#CC7753]" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[var(--t-text)]">Generate Custom Listing Ads</h3>
              <p className="text-xs text-[var(--t-text-muted)]">Describe a property and AI writes tailored ad copy for every platform</p>
            </div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-[var(--t-text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--t-text-muted)]" />}
        </button>

        {open && (
          <div className="px-5 pb-5 space-y-4 border-t border-[var(--t-border)]">
            <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Property Address */}
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                  <MapPin className="w-3 h-3" /> Property Address *
                </label>
                <input
                  type="text"
                  value={form.property_address}
                  onChange={(e) => update("property_address", e.target.value)}
                  placeholder="123 Main St, Denver, CO 80202"
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid="gen-address"
                />
              </div>

              {/* Price */}
              <div>
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                  <DollarSign className="w-3 h-3" /> Price
                </label>
                <input
                  type="text"
                  value={form.price}
                  onChange={(e) => update("price", e.target.value)}
                  placeholder="$525,000"
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid="gen-price"
                />
              </div>

              {/* Beds / Baths / Sqft */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                    <Home className="w-3 h-3" /> Beds
                  </label>
                  <input
                    type="text"
                    value={form.bedrooms}
                    onChange={(e) => update("bedrooms", e.target.value)}
                    placeholder="4"
                    className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                    data-testid="gen-beds"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider mb-1.5 block">Baths</label>
                  <input
                    type="text"
                    value={form.bathrooms}
                    onChange={(e) => update("bathrooms", e.target.value)}
                    placeholder="3"
                    className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                    data-testid="gen-baths"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider mb-1.5 block">Sqft</label>
                  <input
                    type="text"
                    value={form.sqft}
                    onChange={(e) => update("sqft", e.target.value)}
                    placeholder="2,400"
                    className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                    data-testid="gen-sqft"
                  />
                </div>
              </div>

              {/* Key Features */}
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider mb-1.5 block">Key Features / Selling Points</label>
                <textarea
                  value={form.key_features}
                  onChange={(e) => update("key_features", e.target.value)}
                  placeholder="Updated kitchen with quartz countertops, mountain views, finished basement, 2-car garage..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30 resize-none"
                  data-testid="gen-features"
                />
              </div>

              {/* Target Audience */}
              <div>
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                  <Users className="w-3 h-3" /> Target Audience
                </label>
                <input
                  type="text"
                  value={form.target_audience}
                  onChange={(e) => update("target_audience", e.target.value)}
                  placeholder="First-time buyers, young families..."
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid="gen-audience"
                />
              </div>

              {/* Tone */}
              <div>
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider mb-1.5 block">Tone</label>
                <div className="flex gap-1.5 flex-wrap">
                  {toneOptions.map((t) => (
                    <button
                      key={t}
                      onClick={() => update("tone", t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-all ${
                        form.tone === t
                          ? "bg-[#2C4C3B] text-white"
                          : "bg-[var(--t-bg)] text-[var(--t-text-muted)] border border-[var(--t-border)] hover:border-[#2C4C3B]/30"
                      }`}
                      data-testid={`gen-tone-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Listing URL */}
              <div className="md:col-span-2">
                <label className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider mb-1.5 block">Listing URL (optional)</label>
                <input
                  type="text"
                  value={form.listing_url}
                  onChange={(e) => update("listing_url", e.target.value)}
                  placeholder="https://zillow.com/..."
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-[var(--t-bg)] text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid="gen-url"
                />
              </div>
            </div>

            <Button
              onClick={generate}
              disabled={loading || !form.property_address.trim()}
              className="bg-[#CC7753] hover:bg-[#b5654a] text-white h-10 px-6"
              data-testid="gen-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating Ads...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" /> Generate 5 Custom Ads
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ──── Generated Ad Card (text-only, with regenerate) ────
const GeneratedAdCard = ({ ad, onRegenerated }) => {
  const [expanded, setExpanded] = useState(true);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [headlineSuggestion, setHeadlineSuggestion] = useState("");
  const [direction, setDirection] = useState("");

  const QUICK_DIRECTIONS = [
    "More urgency / FOMO",
    "Luxury & aspirational",
    "Casual & conversational",
    "Focus on investment value",
    "Neighborhood & lifestyle",
    "First-time buyer friendly",
  ];

  const handleRegenerate = async (quickDirection) => {
    setRegenLoading(true);
    try {
      const res = await axios.post(`${API}/platform/regenerate-ad`, {
        platform: ad.platform,
        format: ad.format,
        ad_type: ad.type,
        headline_suggestion: headlineSuggestion.trim() || null,
        direction: quickDirection || direction.trim() || null,
      }, { withCredentials: true });

      const newAd = {
        ...ad,
        headline: res.data.headline || ad.headline,
        primary_text: res.data.primary_text || ad.primary_text,
        cta: res.data.cta || ad.cta,
        link_description: res.data.link_description || ad.link_description,
        hook: res.data.hook || ad.hook,
      };
      onRegenerated(ad.id, newAd);
      toast.success("Ad regenerated!");
      setRegenOpen(false);
      setHeadlineSuggestion("");
      setDirection("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to regenerate ad");
    } finally {
      setRegenLoading(false);
    }
  };

  return (
    <Card className="overflow-hidden border-2 border-[#CC7753]/20 hover:shadow-lg transition-shadow bg-gradient-to-br from-[#CC7753]/[0.03] to-transparent" data-testid={`ad-card-${ad.id}`}>
      <CardContent className="p-0">
        {/* Platform-styled preview header */}
        <div className={`p-4 ${ad.type === "story" ? "text-center" : ""}`}>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#CC7753]/10 text-[#CC7753] border border-[#CC7753]/20">
              AI Generated
            </span>
            <PlatformBadge platform={ad.platform} />
            <FormatBadge format={ad.format} />
          </div>

          <h3 className="text-lg font-bold text-[#2C4C3B] whitespace-pre-line leading-snug" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            {ad.headline}
          </h3>
        </div>

        {/* Copy Section */}
        <div className="p-4 space-y-3 border-t border-[#2C4C3B]/10">
          <div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs font-semibold text-[#CC7753] hover:underline"
            >
              {expanded ? "Hide" : "Show"} Full Copy
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            {expanded && (
              <div className="mt-2 space-y-2">
                <div className="bg-[#F9F6F0] rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Primary Text</p>
                  <p className="text-sm text-[#2C4C3B] whitespace-pre-line leading-relaxed">{ad.primary_text}</p>
                </div>
                {ad.link_description && (
                  <div className="bg-[#F9F6F0] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Link Description</p>
                    <p className="text-sm text-[#2C4C3B]">{ad.link_description}</p>
                  </div>
                )}
                {ad.hook && (
                  <div className="bg-[#F9F6F0] rounded-lg p-3">
                    <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">Hook / Opening Line</p>
                    <p className="text-sm text-[#2C4C3B]">{ad.hook}</p>
                  </div>
                )}
                <div className="bg-[#F9F6F0] rounded-lg p-3">
                  <p className="text-xs font-semibold text-[#2C4C3B]/50 uppercase tracking-wider mb-1">CTA Button</p>
                  <p className="text-sm font-semibold text-[#CC7753]">{ad.cta}</p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => copyText(`${ad.headline.replace(/\n/g, " ")}\n\n${ad.primary_text}\n\nCTA: ${ad.cta}${ad.link_description ? `\n${ad.link_description}` : ""}${ad.hook ? `\nHook: ${ad.hook}` : ""}`)}
              data-testid={`copy-${ad.id}`}
            >
              <Copy className="w-3 h-3 mr-1.5" /> Copy All Text
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8 border-[#CC7753]/30 text-[#CC7753] hover:bg-[#CC7753]/5"
              onClick={() => setRegenOpen(!regenOpen)}
              data-testid={`regen-toggle-${ad.id}`}
            >
              <Wand2 className="w-3 h-3 mr-1.5" /> Regenerate
            </Button>
          </div>

          {/* Regenerate Panel */}
          {regenOpen && (
            <div className="mt-2 p-3 rounded-lg border border-[#CC7753]/20 bg-[#CC7753]/[0.03] space-y-3" data-testid={`regen-panel-${ad.id}`}>
              {/* Quick direction pills */}
              <div>
                <p className="text-[10px] font-semibold text-[#2C4C3B]/60 uppercase tracking-wider mb-1.5">Quick Angle</p>
                <div className="flex gap-1.5 flex-wrap">
                  {QUICK_DIRECTIONS.map((d) => (
                    <button
                      key={d}
                      onClick={() => handleRegenerate(d)}
                      disabled={regenLoading}
                      className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-[var(--t-bg)] text-[var(--t-text-muted)] border border-[var(--t-border)] hover:border-[#CC7753]/40 hover:text-[#CC7753] transition-all disabled:opacity-50"
                      data-testid={`quick-dir-${d.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Headline suggestion */}
              <div>
                <label className="text-[10px] font-semibold text-[#2C4C3B]/60 uppercase tracking-wider mb-1 block">
                  Your Headline Idea
                </label>
                <input
                  type="text"
                  value={headlineSuggestion}
                  onChange={(e) => setHeadlineSuggestion(e.target.value)}
                  placeholder='e.g. "Your dream home is waiting in Lakewood"'
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-white text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid={`regen-headline-${ad.id}`}
                />
                <p className="text-[10px] text-[var(--t-text-muted)] mt-0.5">AI will build the full ad copy around your headline</p>
              </div>

              {/* Custom direction */}
              <div>
                <label className="text-[10px] font-semibold text-[#2C4C3B]/60 uppercase tracking-wider mb-1 block">
                  Custom Direction (optional)
                </label>
                <input
                  type="text"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                  placeholder='e.g. "Focus on the school district and family-friendly neighborhood"'
                  className="w-full px-3 py-2 rounded-lg border border-[var(--t-border)] bg-white text-sm text-[var(--t-text)] placeholder:text-[var(--t-text-muted)]/50 focus:outline-none focus:ring-2 focus:ring-[#CC7753]/30"
                  data-testid={`regen-direction-${ad.id}`}
                />
              </div>

              <Button
                size="sm"
                onClick={() => handleRegenerate()}
                disabled={regenLoading || (!headlineSuggestion.trim() && !direction.trim())}
                className="bg-[#CC7753] hover:bg-[#b5654a] text-white text-xs h-8"
                data-testid={`regen-submit-${ad.id}`}
              >
                {regenLoading ? (
                  <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Regenerating...</>
                ) : (
                  <><Wand2 className="w-3 h-3 mr-1.5" /> Regenerate with My Input</>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const AdContent = () => {
  const { user } = useAuth() || {};
  const [filter, setFilter] = useState("all");
  const [generatedAds, setGeneratedAds] = useState([]);

  if (!user) return <Navigate to="/login" replace />;

  const handleAdRegenerated = (adId, newAd) => {
    setGeneratedAds(prev => prev.map(a => a.id === adId ? newAd : a));
  };

  const filtered = filter === "all" ? ADS : ADS.filter((a) => {
    if (filter === "facebook") return a.platform === "Facebook" || a.platform === "Facebook / Instagram";
    if (filter === "instagram") return a.platform === "Instagram" || a.platform === "Facebook / Instagram";
    if (filter === "tiktok") return a.platform === "TikTok";
    if (filter === "carousel") return a.type === "carousel";
    return true;
  });

  return (
    <div className="p-6 lg:p-8" data-testid="ad-content-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[var(--t-text)]">Ad Creative Library</h1>
        <p className="text-[var(--t-text-muted)] text-sm mt-1 max-w-xl">
          Ready-to-use ad copy and creative for Facebook, Instagram, and TikTok. Click "Copy All Text" to grab the copy, or download the images.
        </p>

        {/* UTM reminder */}
        <div className="mt-4 bg-[var(--t-card)]/80 border border-[var(--t-border)] rounded-lg p-3 flex items-start gap-2 max-w-xl">
          <CheckCircle2 className="w-4 h-4 text-[var(--t-text-muted)] mt-0.5 shrink-0" />
          <p className="text-xs text-[var(--t-text-muted)]">
            <strong>Tip:</strong> Use UTM links in your ads for tracking. Find pre-built links in{" "}
            <a href="/platform-admin" className="text-[#CC7753] hover:underline font-medium">Platform Admin &rarr; Analytics</a>.
          </p>
        </div>
      </div>

      {/* AI Generator */}
      <GeneratorForm onGenerated={(ads) => setGeneratedAds(ads)} />

      {/* Generated Ads Section */}
      {generatedAds.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#CC7753]" />
              <h2 className="text-lg font-bold text-[var(--t-text)]">AI-Generated Ads</h2>
              <span className="text-xs text-[var(--t-text-muted)] bg-[var(--t-card)] border border-[var(--t-border)] rounded-full px-2 py-0.5">{generatedAds.length} ads</span>
            </div>
            <button
              onClick={() => setGeneratedAds([])}
              className="text-xs text-[var(--t-text-muted)] hover:text-red-500 flex items-center gap-1 transition-colors"
              data-testid="clear-generated"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {generatedAds.map((ad) => (
              <GeneratedAdCard key={ad.id} ad={ad} onRegenerated={handleAdRegenerated} />
            ))}
          </div>
        </div>
      )}

      {/* Divider if generated ads present */}
      {generatedAds.length > 0 && (
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-[var(--t-border)]" />
          <span className="text-xs font-semibold text-[var(--t-text-muted)] uppercase tracking-wider">Template Library</span>
          <div className="flex-1 h-px bg-[var(--t-border)]" />
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all", label: "All", icon: Layers },
            { key: "facebook", label: "Facebook", icon: Facebook },
            { key: "instagram", label: "Instagram", icon: Instagram },
            { key: "tiktok", label: "TikTok", icon: Square },
            { key: "carousel", label: "Carousel", icon: Smartphone },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                filter === f.key
                  ? "bg-[#2C4C3B] text-white"
                  : "bg-[var(--t-card)] text-[var(--t-text-muted)] border border-[var(--t-border)] hover:border-[#2C4C3B]/30"
              }`}
              data-testid={`filter-${f.key}`}
            >
              <f.icon className="w-3.5 h-3.5" />
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ad Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-8">
        {filtered.map((ad) => (
          <AdCard key={ad.id} ad={ad} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[var(--t-text-muted)] text-sm">
          No ads match this filter.
        </div>
      )}
    </div>
  );
};

export default AdContent;
